import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PlayCircle, Video as VideoIcon, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Videos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-in">
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-3">Videos</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900">Watch & be encouraged.</h1>
        <p className="mt-4 text-lg text-stone-700 max-w-2xl leading-relaxed">
          Sermons, teachings, testimonies, and worship — curated to feed your faith.
        </p>
      </header>

      {loading ? (
        <div className="text-stone-500" data-testid="videos-loading">Loading videos…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-stone-300 bg-white p-12 text-center" data-testid="videos-empty">
          <VideoIcon className="h-10 w-10 text-stone-400 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-stone-900">Coming soon</h3>
          <p className="mt-2 text-stone-600 max-w-md mx-auto">Videos will appear here once published.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-grid">
          {items.map((v) => (
            <button key={v.id} onClick={() => setPlaying(v)} data-testid={`video-card-${v.id}`}
              className="group text-left flex flex-col overflow-hidden rounded-md border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:border-primary">
              <div className="relative aspect-video overflow-hidden bg-stone-100">
                <img src={v.thumbnail_url} alt={v.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent flex items-center justify-center">
                  <PlayCircle className="h-14 w-14 text-white/95 drop-shadow-lg transition-transform group-hover:scale-110" strokeWidth={1.25} />
                </div>
              </div>
              <div className="p-5 flex-1">
                <h3 className="font-serif text-xl text-stone-900 leading-tight">{v.title}</h3>
                {v.description && <p className="mt-2 text-sm text-stone-600 leading-relaxed line-clamp-2">{v.description}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="video-dialog">
          {playing && (
            <>
              <div className="aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1&rel=0`}
                  title={playing.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="p-5">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">{playing.title}</DialogTitle>
                </DialogHeader>
                {playing.description && <p className="mt-2 text-sm text-stone-600 leading-relaxed">{playing.description}</p>}
                <div className="mt-3">
                  <a href={playing.youtube_url} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm">Watch on YouTube <ExternalLink className="ml-1 h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
